import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { EnvelopeSimple, User, PaperPlaneRight, CheckCircle } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { API } from '../../utils/api';
import axios from 'axios';

export default function ContactPage() {
  const { t } = useLanguage();
  const messageRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Scroll to top on mount and focus message field
  useEffect(() => {
    window.scrollTo(0, 0);
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (messageRef.current) {
        messageRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="pp-container">
          <div className="max-w-xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <h1 className="pp-heading-1 text-pp-text mb-4" data-testid="contact-title">
                {t('contact.title')}
              </h1>
              <p className="text-pp-text-secondary">{t('contact.subtitle')}</p>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="pp-card"
            >
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-pp-success-bg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-pp-success-text" weight="fill" />
                  </div>
                  <p className="text-pp-text font-medium">{t('contact.sent')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="pp-label">{t('contact.name')}</Label>
                    <div className="relative">
                      <User 
                        size={18} 
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-pp-text-muted" 
                      />
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="pl-10 pp-input"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="pp-label">{t('contact.email')}</Label>
                    <div className="relative">
                      <EnvelopeSimple 
                        size={18} 
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-pp-text-muted" 
                      />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10 pp-input"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="pp-label">{t('contact.message')}</Label>
                    <Textarea
                      id="message"
                      ref={messageRef}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="pp-input min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="pp-btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {loading ? t('common.loading') : t('contact.send')}
                    <PaperPlaneRight size={18} />
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
